import { AssetPermission, PermissionName } from '../../types/asset-permissions.types'
import { DEFAULT_PAGE_ID } from '@dtx-company/true-common/src/constants/page'
import { EntityTypes } from '../../../../redux/slices/modal'
import { RootState } from '../../../../redux/types'
import { getAssetIdentifiersFromAssetId } from '../../../../components/FlowTeams/utils/asset-permissions.utils'
import { initializeAssetPermissions } from '@dtx-company/inter-app/src/redux/slices/flowteams/assetPermissionsSlice'
import { useAuthState } from '../../../../hooks/useAuthState'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useMemo } from 'react'
import { useGetPermissions } from '@dtx-company/app/shared/code/src/hooks/useGetPermissions'

export const NEW_LOCAL_ASSET_ID = 'batch:NEW'
export const BULK_SHARE_ASSET_ID = 'bulk-share-asset-id'

export function useInitializeAssetPermissionsState(
  assetId: string,
  isWhitelabel?: boolean,
  isCodeSubdomain?: boolean
): void {
  const assetIdentifiers = getAssetIdentifiersFromAssetId(assetId, isWhitelabel, isCodeSubdomain)
  const allAssetPermissions = useSelector(
    (state: RootState) => state.flowteamsReducer.assetPermissions
  )
  const thisAssetsPermissions = assetId ? allAssetPermissions[assetId] : null
  const hasDefinedPermissions = isWhitelabel
    ? Boolean(thisAssetsPermissions?.currentPermissions?.[0])
    : thisAssetsPermissions?.currentPermissions?.[0]?.entityId !== ''
  const hasInitializedAssetPermissions = Boolean(thisAssetsPermissions && hasDefinedPermissions)

  const { allPermissions, loading } = useGetPermissions({
    ...assetIdentifiers,
    shouldFetch:
      assetId !== NEW_LOCAL_ASSET_ID && Boolean(!hasInitializedAssetPermissions && assetIdentifiers)
  })

  const dispatch = useDispatch()

  const { jwt } = useAuthState()

  const currentUserCreatorPermission = useMemo<AssetPermission | null>(
    () =>
      !jwt
        ? null
        : {
            entityId: jwt?.ithacaId,
            entityName:
              jwt?.firstName && jwt?.lastName
                ? `${jwt?.firstName} ${jwt?.lastName}`
                : jwt?.email ?? '',
            entityType: EntityTypes.USER,
            permissionName: PermissionName.CREATOR
          },
    [jwt]
  )

  useEffect(() => {
    const assetHasBeenCreated = assetId !== NEW_LOCAL_ASSET_ID && assetId !== DEFAULT_PAGE_ID

    if (!hasInitializedAssetPermissions && !loading && assetId && assetHasBeenCreated) {
      dispatch(initializeAssetPermissions({ assetId, currentPermissions: allPermissions }))
    }
    if (!hasInitializedAssetPermissions && !assetHasBeenCreated && currentUserCreatorPermission) {
      dispatch(
        initializeAssetPermissions({ assetId, currentPermissions: [currentUserCreatorPermission] })
      )
    }
  }, [
    hasInitializedAssetPermissions,
    loading,
    allPermissions,
    assetId,
    dispatch,
    currentUserCreatorPermission
  ])
}
